import React, { useEffect, useRef } from "react";
import * as THREE from "three";

const WireDartboard = () => {
  const mountRef = useRef(null);
  const dartboardGroupRef = useRef(null);
  const dartsRef = useRef([]);
  const mousePos = useRef({ x: 0, y: 0 });
  const dartCountRef = useRef(0);

  useEffect(() => {
    // Scene setup
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 1000);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    const size = Math.min(window.innerWidth * 0.4, 500);
    renderer.setSize(size, size);
    renderer.setClearColor(0x000000, 0);
    mountRef.current.appendChild(renderer.domElement);

    // Dartboard creation
    const radius = 5;
    const segments = 20; // 20 segments for standard dartboard

    // Create dartboard group
    const dartboardGroup = new THREE.Group();
    scene.add(dartboardGroup);
    dartboardGroupRef.current = dartboardGroup;

    // Rotate dartboard by 9 degrees (20 segments, so each segment is 18 degrees, half segment is 9 degrees)
    dartboardGroup.rotation.z = (9 * Math.PI) / 180;

    // Standard dartboard numbers in clockwise order
    const boardNumbers = [
      20, 1, 18, 4, 13, 6, 10, 15, 2, 17, 3, 19, 7, 16, 8, 11, 14, 9, 12, 5,
    ];

    // Ring radiuses (from outside to inside)
    const doubleRingOuter = radius;
    const doubleRingInner = radius * 0.95;
    const tripleRingOuter = radius * 0.55;
    const tripleRingInner = radius * 0.51;
    const bullOuter = radius * 0.16; // Outer bull
    const bullInner = radius * 0.08; // Inner bull (double bull)

    // Create the main board sections
    for (let i = 0; i < segments; i++) {
      const startAngle = (i * Math.PI * 2) / segments;
      const endAngle = ((i + 1) * Math.PI * 2) / segments;

      // Create section lines
      const lineGeometry = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(0, 0, 0),
        new THREE.Vector3(
          Math.cos(startAngle) * radius,
          Math.sin(startAngle) * radius,
          0
        ),
      ]);

      const line = new THREE.Line(
        lineGeometry,
        new THREE.LineBasicMaterial({
          color: 0xff8f00,
          transparent: true,
          opacity: 0.5,
        })
      );
      dartboardGroup.add(line);
    }

    // Add scoring rings
    const createRing = (innerRadius, outerRadius, opacity = 0.5) => {
      // Outer circle
      const outerGeometry = new THREE.CircleGeometry(outerRadius, segments * 2);
      const outerRing = new THREE.LineLoop(
        new THREE.EdgesGeometry(outerGeometry),
        new THREE.LineBasicMaterial({
          color: 0xff8f00,
          transparent: true,
          opacity,
        })
      );
      dartboardGroup.add(outerRing);

      // Inner circle
      const innerGeometry = new THREE.CircleGeometry(innerRadius, segments * 2);
      const innerRing = new THREE.LineLoop(
        new THREE.EdgesGeometry(innerGeometry),
        new THREE.LineBasicMaterial({
          color: 0xff8f00,
          transparent: true,
          opacity,
        })
      );
      dartboardGroup.add(innerRing);
    };

    // Create all rings
    createRing(doubleRingInner, doubleRingOuter, 0.8); // Double ring
    createRing(tripleRingInner, tripleRingOuter, 0.8); // Triple ring

    // Create filled bullseyes
    const createFilledCircle = (radius, opacity) => {
      // Filled circle
      const geometry = new THREE.CircleGeometry(radius, segments * 2);
      const material = new THREE.MeshBasicMaterial({
        color: "#282424", // Primary color (dark)
        transparent: true,
        opacity: opacity,
      });
      const circle = new THREE.Mesh(geometry, material);
      dartboardGroup.add(circle);

      // Add border
      const border = new THREE.LineLoop(
        new THREE.EdgesGeometry(geometry),
        new THREE.LineBasicMaterial({
          color: 0xff8f00, // Test color (orange)
          transparent: true,
          opacity: 0.8,
        })
      );
      dartboardGroup.add(border);
    };

    // Add outer and inner bullseye (filled)
    createFilledCircle(bullOuter, 1); // Outer bull
    createFilledCircle(bullInner, 1); // Double bull (inner)

    // Position camera to view board from front
    camera.position.z = 15;
    camera.lookAt(0, 0, 0);

    // Mouse interaction
    const handleMouseMove = (event) => {
      const rect = renderer.domElement.getBoundingClientRect();
      mousePos.current = {
        x: ((event.clientX - rect.left) / rect.width) * 2 - 1,
        y: -((event.clientY - rect.top) / rect.height) * 2 + 1,
      };
    };

    const createDart = () => {
      const dartGroup = new THREE.Group();

      // Dart flight - main shape
      const flightGeometry = new THREE.ConeGeometry(0.15, 0.4, 4);
      const flightMaterial = new THREE.MeshBasicMaterial({
        color: 0xff8f00,
        transparent: true,
        opacity: 0.8,
      });
      const flight = new THREE.Mesh(flightGeometry, flightMaterial);
      flight.position.z = 0.2;
      dartGroup.add(flight);

      // Add cross pattern
      const crossGeometry = new THREE.PlaneGeometry(0.3, 0.3);
      const crossMaterial = new THREE.MeshBasicMaterial({
        color: 0xff8f00,
        transparent: true,
        opacity: 0.9,
        side: THREE.DoubleSide,
      });

      // Vertical cross
      const verticalCross = new THREE.Mesh(crossGeometry, crossMaterial);
      verticalCross.position.z = 0.2;
      verticalCross.rotation.z = Math.PI / 4;
      dartGroup.add(verticalCross);

      // Horizontal cross
      const horizontalCross = new THREE.Mesh(crossGeometry, crossMaterial);
      horizontalCross.position.z = 0.2;
      horizontalCross.rotation.z = -Math.PI / 4;
      dartGroup.add(horizontalCross);

      return dartGroup;
    };

    const handleClick = () => {
      if (dartCountRef.current >= 3) return;

      const dart = createDart();

      // Set initial position
      dart.position.set(
        mousePos.current.x * 8,
        mousePos.current.y * 8,
        camera.position.z - 5
      );
      dart.rotation.x = -Math.PI / 2;

      scene.add(dart);

      // Calculate target position on board
      // Adapt how hard it should be to hit
      const vector = new THREE.Vector3(
        mousePos.current.x * 4,
        mousePos.current.y * 4,
        0
      );

      // Animation parameters
      const duration = 200;
      const startTime = Date.now();
      const startPos = dart.position.clone();
      const targetPos = vector;

      const dartAnimation = {
        dart,
        startTime,
        duration,
        startPos,
        targetPos,
        completed: false,
      };

      dartsRef.current.push(dartAnimation);
      dartCountRef.current++;

      if (dartCountRef.current === 3) {
        setTimeout(() => {
          dartsRef.current.forEach((dartAnim) => {
            if (dartAnim.dart && dartAnim.dart.parent) {
              scene.remove(dartAnim.dart);
            }
          });
          dartsRef.current = [];
          dartCountRef.current = 0;
        }, 2000);
      }
    };

    renderer.domElement.addEventListener("mousemove", handleMouseMove);
    renderer.domElement.addEventListener("click", handleClick);

    // Animation
    const animate = () => {
      requestAnimationFrame(animate);

      // Animate darts
      dartsRef.current.forEach((dartAnim, index) => {
        if (!dartAnim || dartAnim.completed) return;

        const elapsed = Date.now() - dartAnim.startTime;
        const progress = Math.min(elapsed / dartAnim.duration, 1);

        // Easing function for smooth animation
        const easeProgress = 1 - Math.pow(1 - progress, 3);

        // Calculate current position
        const currentPos = new THREE.Vector3();
        currentPos.x =
          dartAnim.startPos.x +
          (dartAnim.targetPos.x - dartAnim.startPos.x) * easeProgress;
        currentPos.y =
          dartAnim.startPos.y +
          (dartAnim.targetPos.y - dartAnim.startPos.y) * easeProgress;

        // Add arc to z movement
        const zProgress = Math.sin(progress * Math.PI);
        currentPos.z = dartAnim.startPos.z * (1 - easeProgress) + zProgress * 2;

        dartAnim.dart.position.copy(currentPos);

        // Rotate dart during flight
        // dartAnim.dart.rotation.z = progress * Math.PI * 4;

        if (progress === 1) {
          dartAnim.completed = true;
        }
      });

      renderer.render(scene, camera);
    };
    animate();

    // Handle resize
    const handleResize = () => {
      const size = Math.min(window.innerWidth * 0.4, 500);
      renderer.setSize(size, size);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      mountRef.current?.removeChild(renderer.domElement);
      renderer.domElement.removeEventListener("mousemove", handleMouseMove);
      renderer.domElement.removeEventListener("click", handleClick);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div
      ref={mountRef}
      className="absolute right-0 top-1/2 -translate-y-1/2 mr-20 cursor-crosshair"
      style={{ zIndex: 10 }}
    />
  );
};

export default WireDartboard;
